import type React from 'react';

import { Button, Link, Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core-react/i18n';

import { CommentCard } from './components/CommentCard';

/** "Your comment is live" modal — shows the just-submitted comment and offers to chain into the destination modal. */
export const EntryModal: React.FC<Props> = ({
	isOpen,
	closeButtonAriaLabel,
	decisionMakerName,
	decisionMakerTitle,
	onDismiss,
	onSendToDecisionMaker,
}) => (
	<Modal
		isOpen={isOpen}
		onOpenChange={(open) => !open && onDismiss()}
		onClose={onDismiss}
		closeButtonAriaLabel={closeButtonAriaLabel}
	>
		<ModalHeader
			title={
				<Text variant="heading" size="small" fontWeight="bold">
					<Translate value="corgi.petition_actions.contact_decision_maker.entry.heading" />
				</Text>
			}
		/>
		<ModalBody>
			<Text as="p" variant="body" size="medium" className="text-typography-strong mb-4">
				{decisionMakerTitle ? (
					<Translate
						value="corgi.petition_actions.contact_decision_maker.entry.subheading_with_title"
						replacements={{ decision_maker_name: decisionMakerName, decision_maker_title: decisionMakerTitle }}
					/>
				) : (
					<Translate
						value="corgi.petition_actions.contact_decision_maker.entry.subheading"
						replacements={{ decision_maker_name: decisionMakerName }}
					/>
				)}
			</Text>
			<div className="mb-4 w-full">
				<CommentCard />
			</div>
			<Button
				variant="secondary"
				className="w-full"
				onClick={onSendToDecisionMaker}
				data-testid="contact-decision-maker-entry-cta"
			>
				<Translate
					value="corgi.petition_actions.contact_decision_maker.entry.send_cta_label"
					replacements={{ decision_maker_name: decisionMakerName }}
				/>
			</Button>
			<Text as="p" variant="body" size="small" className="text-typography-weak mt-2 mb-4 text-center">
				<Translate value="corgi.petition_actions.contact_decision_maker.entry.supporting_text" />
			</Text>
			<div className="flex w-full justify-center">
				<Link as="button" bold variant="secondary" onClick={onDismiss} data-testid="contact-decision-maker-entry-skip">
					<Translate value="corgi.petition_actions.contact_decision_maker.continue" />
				</Link>
			</div>
		</ModalBody>
	</Modal>
);

type Props = {
	isOpen: boolean;
	closeButtonAriaLabel: string;
	decisionMakerName: string;
	decisionMakerTitle?: string;
	onDismiss: () => void;
	onSendToDecisionMaker: () => void;
};
